/**
 * TimePicker — an analogue clock face in a popover.
 *
 * ⚠️ **This is the third shape this problem has had, and the first two are both
 * banned.** `<input type="time">` (and `datetime-local`) went first: on the
 * reporting user's Linux/Chromium build the native popup fills the date segment
 * and leaves the time as `--:--`, with no clock and no list anywhere in the
 * widget, so a required time could not be entered at all and an organizer could
 * not create an event. The replacement was a `Select` of half-hour options,
 * which worked and was rejected on sight — "*it's very dull*", and 18:45 could
 * be stored but not entered. **Do not go back to either.** Everything the face
 * needs is drawn here, so it behaves identically in every browser.
 *
 * Three things in here are load-bearing:
 *
 * ⚠️ **1 — The value on the wire is unchanged: `HH:MM`, 24-hour.** The face is a
 * *presentation*. Availability stores a bare local `time`; the organizer's
 * fields join this onto a date and hand it to `toInstant`, which is still the
 * only place a wall clock becomes an instant (Egypt observes DST, so 09:00
 * Cairo is `07:00Z` in January and `06:00Z` in July). No conversion happens in
 * this file, and none should be added to it. `clock.ts` holds the arithmetic.
 *
 * ⚠️ **2 — The dial runs clockwise in Arabic too.** The popover, the toggles and
 * the layout all mirror under RTL; the face does not. A clock that ran
 * anticlockwise because the page reads right-to-left would be a novelty, not a
 * localisation. See `angleFor`.
 *
 * ⚠️ **3 — A dial is a mouse-first interaction, so the keyboard path is not a
 * courtesy.** The face itself is one focusable `slider` moved with the arrow
 * keys — hours by 1, minutes by 1 with Left/Right and by 5 with Up/Down — and
 * the digital readout above it doubles as the stage switch. Someone who cannot
 * use a pointer sets a time with exactly the same number of keystrokes.
 *
 * Committing is explicit. The dial edits a *draft* and only the confirm button
 * (or Enter on the face, from the minute stage) calls `onChange`, so a stray
 * click while aiming does not write a time nobody meant. Escape restores what
 * was there and returns focus to the trigger.
 */
import { Clock as ClockIcon } from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'

import { useLocale } from '@/shared/i18n/useLocale'

import { Button } from './Button'
import { cn } from './cn'
import {
  angleFor,
  fromDisplay,
  handTip,
  minuteMarks,
  parseClock,
  toClockString,
  toDisplay,
  valueAtPoint,
  wrap,
  type Clock,
  type ClockStage,
} from './clock'
import { useFieldProps } from './fieldContext'

/**
 * Every string, per rule 1 of the design system: nothing in here is written in
 * a language. The AM/PM pair is a prop rather than derived from the locale
 * because Arabic wants ص/م, which is not something a component can infer.
 */
export type TimePickerLabels = {
  /** Trigger text while nothing is chosen. */
  placeholder: string
  /** Accessible name for the whole popover. */
  dialog: string
  /** Accessible name for the face, e.g. "Clock face". */
  dial: string
  /** The two stage-switch buttons above the dial. */
  hour: string
  minute: string
  am: string
  pm: string
  confirm: string
  cancel: string
  /** Offered only when `clearable`. */
  clear?: string | undefined
}

export type TimePickerProps = {
  /** `HH:MM`, 24-hour. `''` means nothing chosen. */
  value: string
  onChange: (value: string) => void
  labels: TimePickerLabels
  /**
   * Offer "no time" as a real choice.
   *
   * ⚠️ For an **optional** instant, such as a tier's sales window, where empty
   * means "no bound" rather than "not filled in yet". Without it an organizer
   * can set a sales-close time and then has no way to unset it.
   */
  clearable?: boolean | undefined
  disabled?: boolean | undefined
  /** Minute granularity of the marks on the face. Arrow keys still move by 1. */
  minuteStep?: number | undefined
  /** How the chosen time is written on the trigger. */
  format: (value: string) => string
  className?: string | undefined
  onBlur?: (() => void) | undefined
  name?: string | undefined
}

/** Opened on a stated hour rather than on whatever the clock says now. */
const FALLBACK: Clock = { hour: 9, minute: 0 }

/**
 * Where the numbers sit, as a fraction of the face.
 *
 * ⚠️ **The hand and the numbers share one radius on purpose.** The hand points
 * *at* a number, and the disc that marks the selection is drawn at the same
 * point, so a second radius for the hand would leave the tip short of the label
 * it is naming and the highlight floating off-centre behind it.
 */
const LABEL_RADIUS = 36

export function TimePicker({
  value,
  onChange,
  labels,
  clearable = false,
  disabled = false,
  minuteStep = 5,
  format,
  className,
  onBlur,
  name,
}: TimePickerProps) {
  const field = useFieldProps()
  const { direction } = useLocale()
  const popoverId = useId()

  const [open, setOpen] = useState(false)
  const [stage, setStage] = useState<ClockStage>('hour')
  // The draft. Committed only by the confirm button, so aiming at the dial and
  // missing cannot write a time.
  const [draft, setDraft] = useState<Clock>(() => parseClock(value) ?? FALLBACK)

  const triggerRef = useRef<HTMLButtonElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)

  /**
   * Whether the popover must hang off the trailing edge instead of the leading one.
   *
   * ⚠️ **Measured, not passed in as a prop.** The clipping container is
   * whatever scrolls — a dialog, a card, the page — and a call site cannot
   * know. The ticket-tier modal put a time field in the trailing column of a
   * two-column grid, and the dial ran past the dialog's edge with "Done" and
   * "Cancel" unreachable. `max-w-[calc(100vw-2rem)]` did not help: it guards
   * the *viewport*, and the viewport was not the edge being hit.
   *
   * Measured on open only. The dial does not move while it is open, so there
   * is nothing to keep in sync — and a resize listener here would be a
   * listener per time field on the page.
   */
  const [alignEnd, setAlignEnd] = useState(false)
  const faceRef = useRef<HTMLDivElement>(null)
  // Whether a pointer is being dragged across the face. A ref, not state: it is
  // read inside a listener and must not re-render the dial on every mousemove.
  const draggingRef = useRef(false)

  const parsed = parseClock(value)
  const { hour12, meridiem } = toDisplay(draft.hour)

  /** Open on what the field holds, or on the fallback if it holds nothing. */
  function openPicker() {
    if (disabled) return
    setDraft(parseClock(value) ?? FALLBACK)
    setStage('hour')
    setOpen(true)
  }

  /** Close without committing, and put focus back where it came from. */
  function closePicker() {
    setOpen(false)
    triggerRef.current?.focus()
    onBlur?.()
  }

  function commit(next: Clock) {
    onChange(toClockString(next))
    setOpen(false)
    triggerRef.current?.focus()
    onBlur?.()
  }

  useEffect(() => {
    if (!open) return
    const trigger = popoverRef.current?.parentElement
    if (!trigger) return

    // 288px is `w-72`, the popover's own width.
    const POPOVER_WIDTH = 288
    const box = trigger.getBoundingClientRect()
    // The nearest ancestor that actually clips — a dialog, a card, the page.
    // `offsetParent` walks past static wrappers, which is what we want: only a
    // positioned or scrolling box can crop the popover.
    const container = trigger.closest('[role="dialog"]') ?? document.body
    const bounds = container.getBoundingClientRect()

    const roomAfterStart = bounds.right - box.left
    setAlignEnd(roomAfterStart < POPOVER_WIDTH)
  }, [open])

  // Focus the face when the popover opens, so the arrow keys work without a
  // Tab first — the dial is the only thing anybody opened this to use.
  useEffect(() => {
    if (open) faceRef.current?.focus()
  }, [open])

  // Escape anywhere, and a click outside, both close without committing.
  // ⚠️ `mousedown`, not `click`: a `click` listener fires after the button that
  // opened the popover has already been re-pressed, which closes and reopens it
  // in the same gesture and reads as the popover refusing to open.
  useEffect(() => {
    if (!open) return

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.stopPropagation()
        closePicker()
      }
    }
    function onPointerDown(event: MouseEvent | TouchEvent) {
      const target = event.target as Node | null
      if (!target) return
      if (popoverRef.current?.contains(target)) return
      if (triggerRef.current?.contains(target)) return
      setOpen(false)
      onBlur?.()
    }

    document.addEventListener('keydown', onKeyDown, true)
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('touchstart', onPointerDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown, true)
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('touchstart', onPointerDown)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  /**
   * A pointer on the face → the draft.
   *
   * Reads geometry from the element rather than from a stored size, so it stays
   * correct at the smaller face a 360px viewport gets.
   *
   * ⚠️ **Returns whether it actually moved the hand**, because the centre is a
   * dead zone. A press within a few percent of the middle names no direction at
   * all — `atan2(0, 0)` is 0, so the hand would snap to 12 o'clock on a click
   * the user meant as "nothing" — and the release handler must not advance the
   * stage on a press that set no value.
   *
   * This is also what keeps the component testable. jsdom gives every element a
   * zero-sized `getBoundingClientRect`, so *every* synthetic click resolves to
   * the exact centre; without the dead zone, `user.type` — which clicks before
   * it types — silently set the hour to 3 and jumped to the minute stage before
   * a single key was pressed.
   */
  function applyPointer(clientX: number, clientY: number): boolean {
    const face = faceRef.current
    if (!face) return false
    const box = face.getBoundingClientRect()
    const x = clientX - (box.left + box.width / 2)
    const y = clientY - (box.top + box.height / 2)

    // Proportional to the face, so it is the same dead zone at 360px as at
    // full size rather than a fixed pixel blob that swallows a small dial.
    const deadZone = Math.max(box.width, box.height) * 0.12
    if (Math.hypot(x, y) <= deadZone) return false

    if (stage === 'hour') {
      const index = valueAtPoint(x, y, 12)
      // The face is marked 1–12 with 12 at the top, so index 0 is 12 o'clock.
      setDraft((current) => ({
        ...current,
        hour: fromDisplay(index === 0 ? 12 : index, toDisplay(current.hour).meridiem),
      }))
    } else {
      const step = valueAtPoint(x, y, 60 / minuteStep)
      setDraft((current) => ({ ...current, minute: (step * minuteStep) % 60 }))
    }
    return true
  }

  /**
   * ⚠️ **Hours advance to minutes on pointer *release*, never on move.** The
   * two-stage flow is what makes a dial usable, but switching mid-drag would
   * snatch the face out from under a finger still choosing an hour.
   *
   * A press that landed in the centre dead zone set no hour, so it does not
   * advance either — otherwise a click on the middle of the face skips the user
   * past the stage they were trying to use.
   */
  function onFacePointerUp() {
    const moved = draggingRef.current
    draggingRef.current = false
    if (moved && stage === 'hour') setStage('minute')
  }

  function onFaceKeyDown(event: React.KeyboardEvent) {
    const { key } = event

    if (key === 'Enter' || key === ' ') {
      event.preventDefault()
      // Enter on the hour stage means "done with hours", not "done" — the same
      // thing a click does — so a keyboard user walks the identical two stages.
      if (stage === 'hour') setStage('minute')
      else commit(draft)
      return
    }
    if (key === 'Tab') return

    // ⚠️ Not mirrored under RTL. The dial runs clockwise in both directions, so
    // "Right" is "clockwise" on both — swapping them here would make the key
    // that visibly advances the hand run the clock backwards in Arabic.
    let delta = 0
    if (key === 'ArrowRight') delta = 1
    else if (key === 'ArrowLeft') delta = -1
    else if (key === 'ArrowUp') delta = stage === 'hour' ? 1 : minuteStep
    else if (key === 'ArrowDown') delta = stage === 'hour' ? -1 : -minuteStep
    else if (key === 'Home') delta = 0
    else if (key === 'PageUp') delta = stage === 'hour' ? 3 : 15
    else if (key === 'PageDown') delta = stage === 'hour' ? -3 : -15
    else return

    event.preventDefault()

    if (key === 'Home') {
      setDraft((current) =>
        stage === 'hour'
          ? { ...current, hour: current.hour < 12 ? 0 : 12 }
          : { ...current, minute: 0 },
      )
      return
    }

    setDraft((current) =>
      stage === 'hour'
        ? { ...current, hour: wrap(current.hour, delta, 24) }
        : { ...current, minute: wrap(current.minute, delta, 60) },
    )
  }

  const activeAngle = stage === 'hour' ? angleFor(hour12 % 12, 12) : angleFor(draft.minute, 60)
  const tip = handTip(activeAngle, LABEL_RADIUS)

  const marks =
    stage === 'hour'
      ? Array.from({ length: 12 }, (_, index) => ({
          value: index === 0 ? 12 : index,
          angle: angleFor(index, 12),
          selected: hour12 % 12 === index,
        }))
      : minuteMarks(minuteStep, draft.minute).map((minute) => ({
          value: minute,
          angle: angleFor(minute, 60),
          selected: draft.minute === minute,
        }))

  return (
    <div className={cn('relative', className)}>
      <button
        {...field}
        ref={triggerRef}
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-controls={open ? popoverId : undefined}
        disabled={disabled}
        {...(name ? { 'data-name': name } : {})}
        onClick={() => (open ? closePicker() : openPicker())}
        className={cn(
          'flex h-10 w-full items-center justify-between gap-2 rounded-lg border border-line-strong',
          'bg-surface px-3 text-sm text-fg',
          'aria-invalid:border-danger aria-invalid:ring-1 aria-invalid:ring-danger/30',
          'disabled:cursor-not-allowed disabled:bg-sunken disabled:opacity-70',
        )}
      >
        <span className={cn('truncate text-start', !parsed && 'text-fg-subtle')}>
          {parsed ? format(toClockString(parsed)) : labels.placeholder}
        </span>
        <ClockIcon aria-hidden="true" className="size-4 shrink-0 text-fg-subtle" />
      </button>

      {open ? (
        <div
          ref={popoverRef}
          id={popoverId}
          role="dialog"
          aria-modal="false"
          aria-label={labels.dialog}
          dir={direction}
          className={cn(
            // `start-0` rather than `left-0`, so it hangs off the correct edge
            // of the trigger in Arabic. `max-w-[calc(100vw-2rem)]` is the 360px
            // guard: a fixed-width popover on a narrow phone pushes the page
            // sideways, and horizontal overflow is how a form becomes unusable
            // rather than merely ugly.
            // ⚠️ **`end-0` on the trailing half of a two-column grid.** The
            // popover hung off `start-0` and was clipped by the *dialog's*
            // right edge inside the ticket-tier modal — "Done" and "Cancel"
            // were unreachable. `max-w-[calc(100vw-2rem)]` guards the
            // **viewport**, which is a different edge and did not help: the
            // clipping container was the scrollable dialog.
            //
            // ⚠️ Logical properties throughout (`start`/`end`, never
            // `left`/`right`) — physical ones fail lint, and in Arabic the
            // popover must hang off the mirrored edge.
            'absolute top-full z-50 mt-2 w-72 max-w-[calc(100vw-2rem)]',
            // Anchored to whichever edge leaves room. A trailing-column field
            // opens leftwards (in LTR) so the dial stays inside its container.
            alignEnd ? 'end-0' : 'start-0',
            'rounded-xl border border-line bg-raised p-4 shadow-lg ring-1 ring-edge',
          )}
        >
          {/* The digital readout, which is also the stage switch. Two buttons
              rather than one control: clicking the hour goes back to the hour
              face after the dial has advanced to minutes, which is the only way
              to fix a mis-clicked hour without starting over. */}
          <div className="flex items-center justify-center gap-2">
            <div className="flex items-baseline gap-1 text-3xl font-semibold tabular-nums">
              <button
                type="button"
                aria-label={labels.hour}
                aria-pressed={stage === 'hour'}
                onClick={() => setStage('hour')}
                className={cn(
                  'rounded-md px-1 transition-colors',
                  stage === 'hour' ? 'text-fg' : 'text-fg-subtle',
                )}
              >
                {String(hour12).padStart(2, '0')}
              </button>
              <span aria-hidden="true" className="text-fg-muted">
                :
              </span>
              <button
                type="button"
                aria-label={labels.minute}
                aria-pressed={stage === 'minute'}
                onClick={() => setStage('minute')}
                className={cn(
                  'rounded-md px-1 transition-colors',
                  stage === 'minute' ? 'text-fg' : 'text-fg-subtle',
                )}
              >
                {String(draft.minute).padStart(2, '0')}
              </button>
            </div>

            {/* AM/PM as a toggle pair beside the readout — not a third column
                of numbers, and not on the face, where 24 marks are unreadable
                at this size. */}
            <div className="flex flex-col gap-1 text-xs font-medium">
              {(['am', 'pm'] as const).map((half) => (
                <button
                  key={half}
                  type="button"
                  aria-pressed={meridiem === half}
                  onClick={() =>
                    setDraft((current) => ({
                      ...current,
                      hour: fromDisplay(toDisplay(current.hour).hour12, half),
                    }))
                  }
                  className={cn(
                    'rounded-md border px-2 py-1 transition-colors',
                    meridiem === half
                      ? 'border-brand-fg bg-brand-600 text-fg-onbrand'
                      : 'border-line text-fg-muted hover:bg-sunken',
                  )}
                >
                  {half === 'am' ? labels.am : labels.pm}
                </button>
              ))}
            </div>
          </div>

          {/* The face. One focusable element with a slider role rather than
              twelve buttons: a roving tab stop around a circle is a worse
              keyboard model than "arrows move the hand", and the value a
              screen reader should announce is the time, not the number under
              the pointer. */}
          <div
            ref={faceRef}
            role="slider"
            tabIndex={0}
            aria-label={labels.dial}
            aria-valuemin={0}
            aria-valuemax={stage === 'hour' ? 23 : 59}
            aria-valuenow={stage === 'hour' ? draft.hour : draft.minute}
            aria-valuetext={format(toClockString(draft))}
            onKeyDown={onFaceKeyDown}
            onMouseDown={(event) => {
              draggingRef.current = applyPointer(event.clientX, event.clientY)
            }}
            onMouseMove={(event) => {
              if (draggingRef.current) applyPointer(event.clientX, event.clientY)
            }}
            onMouseUp={onFacePointerUp}
            onMouseLeave={() => {
              draggingRef.current = false
            }}
            onTouchStart={(event) => {
              const touch = event.touches[0]
              draggingRef.current = touch ? applyPointer(touch.clientX, touch.clientY) : false
            }}
            onTouchMove={(event) => {
              const touch = event.touches[0]
              if (touch && draggingRef.current) applyPointer(touch.clientX, touch.clientY)
            }}
            onTouchEnd={onFacePointerUp}
            className={cn(
              'relative mx-auto mt-4 aspect-square w-full max-w-56 touch-none select-none [container-type:size]',
              'rounded-full bg-sunken outline-none',
              'focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2',
            )}
          >
            {/* The hand, in SVG so it is one element rather than a rotated div
                whose transform origin has to be argued with. `viewBox` is the
                0–100 space `handTip` computes in. */}
            <svg
              viewBox="0 0 100 100"
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 size-full"
            >
              <line
                x1="50"
                y1="50"
                x2={50 + (tip.x - 50) * 0.82}
                y2={50 + (tip.y - 50) * 0.82}
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                // ⚠️ `brand-fg`, not `brand-600`. The hand is the selection
                // indicator, so it owes 3:1 against the face (WCAG 1.4.11): dark
                // 600 measures 2.62:1 on sunken, brand-fg 8.69:1. Identical in light.
                className="text-brand-fg"
              />
              <circle cx="50" cy="50" r="2.5" className="fill-current text-brand-fg" />
              {/* The tip keeps the 600 *fill* — it carries the selected numeral
                  in white (7.42:1 dark), which brand-fg could not — and gets a
                  brand-fg *ring* so its edge, too, clears 3:1 on the face. */}
              <circle
                cx={tip.x}
                cy={tip.y}
                r="9"
                strokeWidth="1.5"
                className="fill-brand-600 stroke-brand-fg"
              />
            </svg>

            {marks.map((mark) => {
              const point = handTip(mark.angle, LABEL_RADIUS)
              return (
                <button
                  key={mark.value}
                  type="button"
                  tabIndex={-1}
                  aria-hidden="true"
                  /**
                   * ⚠️ **Placed by transform, not by an inset — and this is an
                   * RTL bug that shipped once already.**
                   *
                   * `insetInlineStart` measures from the *right* edge under
                   * `dir="rtl"`, so the same percentage put every number on the
                   * wrong side of the face while `-translate-x-1/2` kept
                   * pulling the same way: measured in Arabic, 12 sat 28px left
                   * of centre and 3 sat at -109 instead of +81. The face is the
                   * one thing here that must **not** mirror — a clock runs
                   * clockwise in Cairo too — so it cannot be positioned with a
                   * logical property.
                   *
                   * `translate` is direction-agnostic, and the box is pinned by
                   * `left: 0` alone — `inset-0` is over-constrained against a
                   * fixed `size-7`, and RTL resolves that by honouring `right`,
                   * which parked every number against the face's right edge
                   * (measured: +196px). The offsets are a percentage of the
                   * face's own box (`cqw`/`cqh`, hence its `container-type`),
                   * and the `-50%` pair is the centring: the point is where the
                   * label's middle goes, not its corner.
                   *
                   * ⚠️ **The button *is* the chip — it must not be a full-face
                   * overlay.** Wrapping the number in an `inset-0` button made
                   * all twelve marks cover the whole dial, so the last one
                   * rendered swallowed every click and only it could be picked.
                   * jsdom cannot see that (no layout, no hit-testing); the
                   * browser suite caught it.
                   */
                  className={cn(
                    'absolute top-0 z-10 flex size-7 items-center justify-center',
                    'rounded-full text-xs tabular-nums transition-colors',
                    mark.selected
                      ? 'font-semibold text-fg-onbrand'
                      : 'text-fg-muted hover:bg-brand-100',
                  )}
                  style={{
                    // `left`, not `insetInlineStart`, and an explicit LTR
                    // context: see the note above. Both are physical on purpose
                    // — the face is the one thing that must not mirror.
                    left: 0,
                    direction: 'ltr',
                    transform: `translate(calc(${point.x}cqw - 50%), calc(${point.y}cqh - 50%))`,
                  }}
                  onClick={() => {
                    if (stage === 'hour') {
                      setDraft((current) => ({
                        ...current,
                        hour: fromDisplay(mark.value, toDisplay(current.hour).meridiem),
                      }))
                      setStage('minute')
                    } else {
                      setDraft((current) => ({ ...current, minute: mark.value }))
                    }
                  }}
                >
                  {String(mark.value).padStart(stage === 'hour' ? 1 : 2, '0')}
                </button>
              )
            })}
          </div>

          <div className="mt-4 flex items-center gap-2">
            {clearable && labels.clear ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  onChange('')
                  setOpen(false)
                  triggerRef.current?.focus()
                  onBlur?.()
                }}
              >
                {labels.clear}
              </Button>
            ) : null}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="ms-auto"
              onClick={closePicker}
            >
              {labels.cancel}
            </Button>
            <Button type="button" size="sm" onClick={() => commit(draft)}>
              {labels.confirm}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  )
}
